// Canonical consent layer for AuthorityLayer.
// - Stores user choice in localStorage with a 12-month TTL.
// - Pushes Google Consent Mode v2 updates to gtag.
// - Lazily injects Refgrow and IndexPointer once analytics consent is granted.
// - Region-aware: EU/EEA/UK/CH require an explicit banner choice; all other
//   regions auto-grant analytics on first visit (unless GPC is signalled).
// - Advertising is independently selected; region auto-grants analytics only.
// - Production-host gated; no-op on staging / preview / localhost.

export type ConsentState = "granted" | "denied";
export type ConsentSource = "user" | "auto-region";

export interface StoredConsent {
  analytics: ConsentState;
  advertising?: ConsentState; // legacy records imply denied advertising
  ts: string; // ISO timestamp
  source?: ConsentSource;
}

const STORAGE_KEY = "al_consent_v1";
const REGION_CACHE_KEY = "al_region_cc";
const TTL_MS = 1000 * 60 * 60 * 24 * 365; // 12 months
const PRODUCTION_HOSTS = new Set(["authoritylayer.app", "www.authoritylayer.app"]);
const REFGROW_SRC = "https://scripts.refgrowcdn.com/latest.js";
const REFGROW_PROJECT_ID = "852";

const REQUEST_OPEN_EVENT = "al:consent:open";
const CHANGE_EVENT = "al:consent:change";

// EU27 + EEA add-ons (IS, LI, NO) + UK + CH.
// Visitors from these countries require an explicit banner choice.
const CONSENT_REQUIRED_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE",
  "IS", "LI", "NO",
  "GB", "CH",
]);

// Timezone fallback used only when /cdn-cgi/trace is unavailable.
// Any Europe/* timezone is treated as required EXCEPT this allow-list of
// definitely-non-EU European zones. Plus a few Atlantic/Arctic zones that
// belong to EEA states.
const EU_EXTRA_TIMEZONES = new Set([
  "Atlantic/Reykjavik", // IS
  "Atlantic/Faroe",     // DK
  "Atlantic/Madeira",   // PT
  "Atlantic/Azores",    // PT
  "Atlantic/Canary",    // ES
  "Arctic/Longyearbyen",// NO
]);
const NON_EU_EUROPE_TIMEZONES = new Set([
  "Europe/Istanbul", "Europe/Moscow", "Europe/Minsk", "Europe/Kiev",
  "Europe/Kyiv", "Europe/Simferopol", "Europe/Volgograd", "Europe/Samara",
  "Europe/Saratov", "Europe/Astrakhan", "Europe/Kaliningrad",
  "Europe/Ulyanovsk",
]);

export function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTS.has(window.location.hostname);
}

export function getConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parseStoredConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Strict validation shared in behavior with the synchronous HTML bootstrap. */
export function parseStoredConsent(value: unknown, now = Date.now()): StoredConsent | null {
  if (!value || typeof value !== "object") return null;
  const c = value as Partial<StoredConsent>;
  if (c.analytics !== "granted" && c.analytics !== "denied") return null;
  if (typeof c.ts !== "string") return null;
  const age = now - Date.parse(c.ts);
  if (!Number.isFinite(age) || age < 0 || age >= TTL_MS) return null;
  return {
    analytics: c.analytics,
    advertising: c.source === "auto-region" ? "denied" : c.advertising === "granted" ? "granted" : "denied",
    ts: c.ts,
    source: c.source === "auto-region" ? "auto-region" : "user",
  };
}

export function hasAnalyticsConsent(): boolean {
  return !isGpcSignaled() && getConsent()?.analytics === "granted";
}

export function hasAdvertisingConsent(): boolean {
  return !isGpcSignaled() && getConsent()?.advertising === "granted";
}

export function googleConsentSignals(analytics: ConsentState, advertising: ConsentState) {
  return {
    analytics_storage: analytics,
    ad_storage: advertising,
    ad_user_data: advertising,
    ad_personalization: advertising,
  };
}

/** True when the visitor's browser signals Global Privacy Control. */
export function isGpcSignaled(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator as unknown as { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;
}

type GtagFn = (...args: unknown[]) => void;
function gtag(): GtagFn | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { gtag?: GtagFn };
  return typeof w.gtag === "function" ? w.gtag : null;
}

export function setConsent(
  analytics: ConsentState,
  source: ConsentSource = "user",
  advertising: ConsentState = "denied",
): void {
  if (typeof window === "undefined") return;
  if (isGpcSignaled()) { analytics = "denied"; advertising = "denied"; }
  if (source === "auto-region") advertising = "denied";
  const value: StoredConsent = {
    analytics,
    advertising,
    ts: new Date().toISOString(),
    source,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore quota / privacy-mode failures
  }

  const fn = gtag();
  if (fn) {
    fn("consent", "update", googleConsentSignals(analytics, advertising));
  }

  if (analytics === "granted") {
    loadRefgrow();
  }
  if (advertising === "granted") {
    void import("./indexpointer").then((m) => {
      if (hasAdvertisingConsent()) m.loadIndexPointer();
    });
  }

  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: value }));
  } catch {
    /* ignore */
  }
}

/** Idempotent Refgrow injector. Only loads on production hosts. */
export function loadRefgrow(): void {
  if (!isProductionHost() || !hasAnalyticsConsent()) return;
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${REFGROW_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = REFGROW_SRC;
  s.async = true;
  s.defer = true;
  s.dataset.projectId = REFGROW_PROJECT_ID;
  document.head.appendChild(s);
}

/** Imperative trigger so the footer link can re-open the banner. */
export function openConsentBanner(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(REQUEST_OPEN_EVENT));
  } catch {
    /* ignore */
  }
}

export function onConsentOpenRequest(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler();
  window.addEventListener(REQUEST_OPEN_EVENT, listener);
  return () => window.removeEventListener(REQUEST_OPEN_EVENT, listener);
}

export function onConsentChange(handler: (c: StoredConsent) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<StoredConsent>).detail;
    if (detail) handler(detail);
  };
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}

// ---------------------------------------------------------------------------
// Region detection
// ---------------------------------------------------------------------------

interface RegionInfo {
  /** ISO-3166-1 alpha-2 country code, or null when unknown. */
  country: string | null;
  /** True when visitor falls under EU/EEA/UK/CH consent rules. */
  required: boolean;
  /** Where the signal came from. */
  source: "cf-trace" | "session-cache" | "timezone-fallback" | "unknown-failsafe";
}

function inferRequiredFromTimezoneLocale(): RegionInfo {
  try {
    const tz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (EU_EXTRA_TIMEZONES.has(tz)) {
      return { country: null, required: true, source: "timezone-fallback" };
    }
    if (tz.startsWith("Europe/") && !NON_EU_EUROPE_TIMEZONES.has(tz)) {
      return { country: null, required: true, source: "timezone-fallback" };
    }
    // Locale hint — long shot but cheap.
    const lang = (navigator?.language || "").toLowerCase();
    const euLangPrefixes = [
      "de", "fr", "it", "es", "nl", "pl", "pt", "sv", "da", "fi", "el",
      "cs", "hu", "ro", "bg", "hr", "sk", "sl", "et", "lv", "lt", "mt",
      "ga", "is", "no", "nb", "nn",
    ];
    if (euLangPrefixes.some((p) => lang === p || lang.startsWith(`${p}-`))) {
      return { country: null, required: true, source: "timezone-fallback" };
    }
    // Only unambiguous non-European timezones can bypass the banner.
    if (/^(America|Australia|Pacific|Asia|Africa)\//.test(tz)) {
      return { country: null, required: false, source: "timezone-fallback" };
    }
    return { country: null, required: true, source: "unknown-failsafe" };
  } catch {
    // Anything weird -> fail-safe to required.
    return { country: null, required: true, source: "unknown-failsafe" };
  }
}

let regionPromise: Promise<RegionInfo> | null = null;

/**
 * Resolve the visitor's country via Cloudflare's `/cdn-cgi/trace` endpoint.
 * Result is cached for the tab in sessionStorage and de-duplicated in-memory.
 * Falls back to timezone/locale inference if the trace request fails.
 * Unknown -> required (fail-safe).
 */
export function detectRegion(): Promise<RegionInfo> {
  if (regionPromise) return regionPromise;
  if (typeof window === "undefined") {
    regionPromise = Promise.resolve({
      country: null,
      required: true,
      source: "unknown-failsafe",
    });
    return regionPromise;
  }

  regionPromise = (async (): Promise<RegionInfo> => {
    // Session cache (set by index.html bootstrap or a previous call).
    try {
      const cached = window.sessionStorage.getItem(REGION_CACHE_KEY);
      if (cached) {
        const cc = cached.toUpperCase();
        return {
          country: cc || null,
          required: cc ? CONSENT_REQUIRED_COUNTRIES.has(cc) : true,
          source: "session-cache",
        };
      }
    } catch {
      /* ignore */
    }

    // Cloudflare edge geo.
    try {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 1500);
      const res = await fetch("/cdn-cgi/trace", {
        signal: ctrl.signal,
        credentials: "omit",
        cache: "no-store",
      });
      window.clearTimeout(timer);
      if (res.ok) {
        const text = await res.text();
        const match = /(?:^|\n)loc=([A-Za-z]{2})/.exec(text);
        if (match) {
          const cc = match[1].toUpperCase();
          try {
            window.sessionStorage.setItem(REGION_CACHE_KEY, cc);
          } catch {
            /* ignore */
          }
          return {
            country: cc,
            required: CONSENT_REQUIRED_COUNTRIES.has(cc),
            source: "cf-trace",
          };
        }
      }
    } catch {
      /* fallthrough to timezone */
    }

    return inferRequiredFromTimezoneLocale();
  })();
  return regionPromise;
}

/** Convenience boolean. EU/EEA/UK/CH -> true. Unknown -> true (fail-safe). */
export async function isConsentRequiredRegion(): Promise<boolean> {
  return (await detectRegion()).required;
}

/**
 * Bootstrap. Call once at app start.
 * - If consent already stored, lazy-load downstream tags when granted and exit.
 * - If GPC is signalled, do nothing (analytics stays denied, banner suppressed).
 * - If region is required (EU/EEA/UK/CH/unknown), do nothing — the banner
 *   will collect an explicit choice.
 * - Otherwise (non-EU, no GPC, no stored choice) → write a synthetic
 *   `granted` record tagged with source: "auto-region". This triggers the
 *   gtag consent update plus Refgrow. IndexPointer needs advertising consent.
 *
 * Important: never overwrites an existing stored consent record (denied OR
 * granted), so user choices and explicit opt-outs are preserved.
 */
export async function initConsent(): Promise<void> {
  if (!isProductionHost()) return;

  if (isGpcSignaled()) {
    gtag()?.("consent", "update", googleConsentSignals("denied", "denied"));
    return;
  }
  const existing = getConsent();
  if (existing) {
    gtag()?.("consent", "update", googleConsentSignals(existing.analytics, existing.advertising ?? "denied"));
    if (hasAnalyticsConsent()) loadRefgrow();
    if (hasAdvertisingConsent()) {
      void import("./indexpointer").then((m) => {
        if (hasAdvertisingConsent()) m.loadIndexPointer();
      });
    }
    return;
  }

  const { required } = await detectRegion();
  if (required || isGpcSignaled() || getConsent()) return;
  setConsent("granted", "auto-region", "denied");
}
